import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';

/**
 * GlobalErrorBoundary - Catches all uncaught errors in the app
 *
 * IMPORTANT: This component renders OUTSIDE the Router, so ErrorFallback
 * must NOT use useNavigate or any Router hooks. Use window.location instead.
 */

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const isDev = import.meta.env.MODE === 'development';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ maxWidth: '512px', width: '100%' }}>
        {/* ODV Logo */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
          <img src="/odv-logo-car.png" alt="ODV Logo" style={{ width: '128px', height: 'auto' }} />
        </div>

        {/* Error Card */}
        <div style={{ backgroundColor: '#111', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '8px', padding: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff', margin: 0 }}>Something went wrong</h1>
              <p style={{ color: '#9ca3af', marginTop: '4px', fontSize: '14px' }}>The application encountered an unexpected error</p>
            </div>
          </div>

          {/* Error Details (Dev Only) */}
          {isDev && error && (
            <div style={{ marginBottom: '24px', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#f87171' }}>
                {error.name}: {error.message}
              </div>
            </div>
          )}

          {/* Production Error Message */}
          {!isDev && (
            <div style={{ marginBottom: '24px', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
              <p style={{ color: '#d1d5db', fontSize: '14px', margin: 0 }}>
                This error has been reported to our team.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={resetErrorBoundary}
              style={{ flex: 1, padding: '12px 16px', backgroundColor: '#aea36d', color: '#000', fontWeight: 600, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
            >
              Try Again
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{ flex: 1, padding: '12px 16px', backgroundColor: '#1f2937', color: '#fff', border: '1px solid #374151', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
            >
              Go Home
            </button>
          </div>

          <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
            If this problem persists, contact{' '}
            <a href="mailto:support@opendoorvalet.com" style={{ color: '#aea36d' }}>
              support@opendoorvalet.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// A deploy invalidates every lazy chunk under an OPEN session: the browser holds
// yesterday's index, the new build hashes the chunk differently, and the first
// lazy route the client opens 404s. main.jsx already reloads once on that error —
// but a React.lazy rejection never reaches window.onerror / unhandledrejection; it
// lands HERE, in the boundary, which used to render "Something went wrong" over a
// perfectly healthy portal. Seen live 2026-09-16: a client opened Invoices four
// minutes after a peer's [deploy-truest] and got the error page (Doug: "continue to
// make me look like a fool"). Same once-per-session guard as main.jsx so a genuinely
// missing chunk cannot loop.
function isStaleChunkError(error) {
  const message = typeof error === 'string' ? error : error?.message || '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError')
  );
}

function reloadOnceForFreshAssets() {
  const reloadKey = 'chunk_error_reloaded';
  try {
    if (sessionStorage.getItem(reloadKey)) return false;
    sessionStorage.setItem(reloadKey, 'true');
  } catch {
    return false;
  }
  setTimeout(() => window.location.reload(), 300);
  return true;
}

const GlobalErrorBoundary = ({ children }) => {
  const handleError = (error, errorInfo) => {
    if (isStaleChunkError(error) && reloadOnceForFreshAssets()) {
      console.warn('[GlobalErrorBoundary] stale chunk after a deploy — reloading once for fresh assets');
      return;
    }
    console.error('[GlobalErrorBoundary] Uncaught error:', error, errorInfo);

    // Report to Sentry in production
    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={() => {
        // Clear any persisted error state
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default GlobalErrorBoundary;
